// See hooks.ts. Owned by the places family of kits (docs/186 EA7): the ruins, town, docks, courtyard and
// graveyard outdoors, the house, ship, barrow and mine indoors. The town, courtyard, house, ship, barrow
// and mine draw with the shared walls (walls.ts) from their look.json; the ruins, the docks' harbour and
// the graveyard's grounds are their own (places-*.ts).
import type { Canvas } from './canvas';
import { C, type Ctx } from './context';
import { groundOf, type Ground } from './ground';
import type { Hooks } from './hooks';
import { docksBanks, docksGroundOf, isDocks } from './places-docks';
import { graveAfterGround, graveGroundOf, graveStanding, isGraveyard } from './places-graveyard';
import { isRuin, ruinAfterGround, ruinGroundOf, ruinWalls } from './places-ruins';

export const hooks: Hooks = {
  ground(ctx, x, y, s, g) {
    if (isRuin(ctx)) return ruinGroundOf(ctx, x, y, s, g);
    if (isDocks(ctx)) return docksGroundOf(ctx, x, y, s, g);
    if (isGraveyard(ctx)) return graveGroundOf(ctx, x, y, s, g);
    return undefined;
  },
  banks(ctx, out, grounds) {
    // the ruins edge their water with the wet line from `ruin_tones`, no foam or pale rim (docs/166 C2)
    if (isRuin(ctx)) return true;
    if (isDocks(ctx)) { docksBanks(ctx, out, grounds); return true; }
    return false;
  },
  afterGround(ctx, out, grounds) {
    if (PLACES.has(ctx.set.kit) && !isRuin(ctx)) pillarFloors(ctx, out, grounds);
    if (isRuin(ctx)) ruinAfterGround(ctx, out);
    else if (isGraveyard(ctx)) graveAfterGround(ctx, out, grounds);
  },
  walls(ctx, ground) {
    return isRuin(ctx) ? ruinWalls(ctx, ground) : undefined;
  },
  afterStanding(ctx, out, _grounds, standing) {
    if (isGraveyard(ctx)) graveStanding(ctx, out, standing);
  },
};

const PLACES = new Set(['ruins', 'town', 'docks', 'courtyard', 'graveyard', 'house', 'ship', 'barrow', 'mine']);

/**
 * A lone wall in a room is a pillar the painter stands from `stand` (walls.ts), but the ground pass skips
 * every wall square of a built kit, so it stood on the dark under the walls: lay the floor under it.
 */
function pillarFloors(ctx: Ctx, out: Canvas, grounds: ReadonlyArray<Ground | undefined>): void {
  const { sq } = ctx;
  if (!ctx.built || !ctx.has('stand')) return;
  for (let y = 0; y < sq.h; y++) for (let x = 0; x < sq.w; x++) {
    if (sq.at(x, y) !== 'wall' || sq.isWall(x - 1, y) || sq.isWall(x + 1, y) || sq.isWall(x, y - 1) || sq.isWall(x, y + 1)) continue;
    // the room's own floor when the painter gave it one (EA13)
    const g = grounds[y * sq.w + x] ?? groundOf(ctx, 'floor', 'floor', x, y);
    if (g) out.draw(g.piece, x * C, y * C, g.turn, true);
  }
}
